// Graph visualization helper functions
// Utilities for positioning, styling, and calculating graph element properties

import { graph } from './designTokens';

/**
 * Calculate the left position for security badges
 * Positions badges so the leftmost (index 0) aligns with the left edge
 * @param {number} index - The index of the badge (0-based)
 * @param {string} position - Badge position ('top' for input, 'bottom' for output)
 * @returns {number} The left position in pixels
 */
export const getSecurityBadgeLeftPosition = (index, position = 'top') => {
  const config = position === 'bottom'
    ? graph.badges.securityBadges.outputBadges
    : graph.badges.securityBadges.inputBadges;
  const { horizontalSpacing } = config;

  // Start at left edge (0), then move right by horizontalSpacing for each badge
  return index * horizontalSpacing;
};

/**
 * Get the appropriate node configuration based on node type and category
 * @param {string} type - Node type (e.g., 'agent', 'tool', 'framework')
 * @param {string} category - Node category ('entity' or 'concept')
 * @returns {Object} Node configuration with stroke color, icon, and label
 */
export const getNodeConfig = (type, category = 'entity') => {
  if (!type) throw new Error('getNodeConfig requires type parameter');
  if (category !== 'entity' && category !== 'concept') {
    throw new Error('category must be "entity" or "concept"');
  }

  const config = graph.nodes[category][type];
  if (!config) {
    // Fallback to 'unspecified' type for forward compatibility
    return graph.nodes[category]['unspecified'] || { stroke: '#9E9E9E', icon: null, label: 'UNSPECIFIED' };
  }

  return config;
};

/**
 * Get the border style based on node category
 * @param {string} category - Node category ('entity' or 'concept')
 * @returns {string} CSS border style value
 */
export const getNodeBorderStyle = (category) => {
  return category === 'concept' ? 'dashed' : 'solid';
};

/**
 * Get the appropriate box shadow based on node state
 * @param {string} state - Node state ('default', 'mainAgent', 'selected', 'danger')
 * @param {string} strokeColor - The node's stroke color (for mainAgent state)
 * @returns {string} CSS box-shadow value
 */
export const getNodeBoxShadow = (state, strokeColor = null) => {
  if (!state) return graph.nodes.states.default.boxShadow;

  if (state === 'mainAgent' && strokeColor) {
    return graph.nodes.states.mainAgent(strokeColor).boxShadow;
  }

  return graph.nodes.states[state]?.boxShadow || graph.nodes.states.default.boxShadow;
};

/**
 * Get security badges for a node based on its data
 * @param {Object} nodeData - Node data object
 * @returns {Array} Array of security badge types
 */
export const getSecurityBadgesForNode = (nodeData) => {
  if (!nodeData) return [];

  const securityBadges = [];

  // Check for prompt injection
  if (nodeData.hasPromptInjection) {
    securityBadges.push('prompt_injection');
  }

  // Check for data exfiltration
  if (nodeData.hasDataExfiltration) {
    securityBadges.push('data_exfiltration');
  }

  return securityBadges;
};

/**
 * Get the security badge configuration (icon and label) for a badge type
 * @param {string} badgeType - Security badge type (e.g., 'prompt_injection', 'data_exfiltration')
 * @returns {Object} Badge configuration with icon and label
 */
export const getSecurityBadgeConfig = (badgeType) => {
  if (!badgeType) return null;
  return graph.badges.securityBadges.types[badgeType] || null;
};

/**
 * Get the edge style configuration based on connection type and status
 * @param {string} connectionType - 'entity' or 'concept'
 * @param {string} status - 'normal', 'warning', or 'danger'
 * @param {number} throughput - Number of operations (for width calculation)
 * @returns {Object} Edge style configuration { stroke, strokeWidth, strokeDasharray }
 */
export const getEdgeStyle = (connectionType = 'entity', status = 'normal', throughput = 0) => {
  if (connectionType !== 'entity' && connectionType !== 'concept') {
    throw new Error('connectionType must be "entity" or "concept"');
  }

  // Determine stroke width based on throughput
  let strokeWidth;
  if (throughput < 100) {
    strokeWidth = graph.edges.strokeWidth.small;
  } else if (throughput < 1000) {
    strokeWidth = graph.edges.strokeWidth.medium;
  } else {
    strokeWidth = graph.edges.strokeWidth.large;
  }

  // Concept edges always use small width
  if (connectionType === 'concept') {
    strokeWidth = graph.edges.strokeWidth.small;
  }

  return {
    stroke: graph.edges.colors[status] || graph.edges.colors.normal,
    strokeWidth,
    strokeDasharray: graph.edges[connectionType].strokeDasharray,
  };
};

/**
 * Determine edge status based on KPI thresholds
 * @param {Object} kpis - Edge KPI data { p95LatencyMs, errorRatePct, securityHigh }
 * @returns {string} Status ('normal', 'warning', or 'danger')
 */
export const getEdgeStatus = (kpis) => {
  if (!kpis) return 'normal';

  const { p95LatencyMs = 0, errorRatePct = 0, securityHigh = 0 } = kpis;

  // Danger: security issues, very high error rate, or very high latency
  if (securityHigh > 0 || errorRatePct > 10 || p95LatencyMs > 1500) {
    return 'danger';
  }

  // Warning: moderate error rate or moderate latency
  if (errorRatePct > 5 || p95LatencyMs > 1000) {
    return 'warning';
  }

  return 'normal';
};

/**
 * Get input detections for a node (system and user messages)
 * @param {Object} nodeData - Node data object with detections array
 * @returns {Array} Array of input detections
 */
export const getInputBadgesForNode = (nodeData) => {
  if (!nodeData || !Array.isArray(nodeData.detections)) return [];

  return nodeData.detections.filter(d =>
    d.prompt_type === 'system' || d.prompt_type === 'user'
  );
};

/**
 * Get output detections for a node (assistant messages)
 * @param {Object} nodeData - Node data object with detections array
 * @returns {Array} Array of output detections
 */
export const getOutputBadgesForNode = (nodeData) => {
  if (!nodeData || !Array.isArray(nodeData.detections)) return [];

  return nodeData.detections.filter(d =>
    d.prompt_type === 'assistant'
  );
};

/**
 * Get total badge count for detections
 * @param {Array} detections - Array of detection objects
 * @returns {number} Total count
 */
export const getBadgeTotalCount = (detections) => {
  if (!Array.isArray(detections)) return 0;
  return detections.length;
};
